import type { BadgeTone } from '@shared/ui';

import type { FlagPurpose } from './flag';

/**
 * How a purpose renders.
 *
 * Its own module rather than a constant exported from a page, for the reason
 * `lifecycle.ts` gives: a file that exports both components and values loses
 * Fast Refresh for everything in it, and editing a label would remount the
 * flags table and drop whatever filter the operator had typed.
 */

export const PURPOSE_LABEL: Record<FlagPurpose, string> = {
    release: 'Release',
    operational: 'Operational',
    entitlement: 'Entitlement',
};

/**
 * Deliberately not `brand`, which `flag-type.ts` already spends on `salesforce`:
 * two badges in the same row, same colour, meaning things on different axes is
 * worse than a duller palette.
 */
export const PURPOSE_TONE: Record<FlagPurpose, BadgeTone> = {
    release: 'info',
    operational: 'warning',
    entitlement: 'active',
};

/** Chip order: temporary, then permanent, then sold — the life a flag can have, shortest first. */
export const PURPOSE_ORDER: readonly FlagPurpose[] = ['release', 'operational', 'entitlement'];

/**
 * How a purpose is *chosen*, as opposed to how it renders once chosen.
 *
 * One sentence each, because the words alone do not separate them — `release`
 * and `operational` both sound like "engineering's", and the difference that
 * matters is whether the flag is ever meant to be deleted.
 *
 * Here rather than in either dialog because both ask the same question: the
 * create form at birth, the edit form for the flag that changes what it is for.
 * Two copies of this list would drift, and the drift would be two operators
 * reading different definitions of the same word.
 */
export const PURPOSE_OPTIONS: readonly { value: FlagPurpose; label: string }[] = [
    { value: 'release', label: 'Release — temporary, deleted at GA' },
    { value: 'operational', label: 'Operational — permanent, never sold' },
    { value: 'entitlement', label: 'Entitlement — part of what a museum buys' },
];
